import type { Translation } from "../../../../hooks/useTranslations";
import { LATERAL_RESISTANCE_CONFIG } from "../../../../http/lock_calculator/types";
import { lateralResistanceLabels } from "./structuralLabels";

/** `value` null is the exposed connection: no notch (what the 3D draws for no configuration). */
export type LateralResistanceOption = { value: number | null; label: string };

const { HeaderColumnNotch, HeaderBeamNotch, JoistBeamNotch, InclinedFasteners, LockStop } = LATERAL_RESISTANCE_CONFIG;

/** What each header orientation allows: its own notch plus the configurations shared by both. */
const COLUMN_CONFIGS: number[] = [HeaderColumnNotch, JoistBeamNotch, InclinedFasteners, LockStop];
const BEAM_CONFIGS: number[] = [HeaderBeamNotch, JoistBeamNotch, InclinedFasteners, LockStop];

/** The configurations that hide the connection in a pocket cut into the timber. */
const NOTCHES: number[] = [HeaderColumnNotch, HeaderBeamNotch, JoistBeamNotch];

/**
 * The lateral resistance options for a header orientation (none yet: every configuration). Without a lateral
 * load the field is not a calculation choice but where the connection sits: the notches, which hide it in the
 * pocket, or exposed, which stands for every configuration that leaves it out (none, inclined fasteners, lock stop).
 */
export const buildLateralResistanceOptions = (
  t: Translation,
  headerOrientation: string | null | undefined,
  hasLateralLoad: boolean,
): LateralResistanceOption[] => {
  const labels = lateralResistanceLabels(t);
  const everyOption = Object.entries(labels).map(([value, label]) => ({ value: Number(value), label }));
  const forOrientation = headerOrientation === "column" ? COLUMN_CONFIGS : BEAM_CONFIGS;
  const allowed = headerOrientation ? everyOption.filter(({ value }) => forOrientation.includes(value)) : everyOption;
  if (hasLateralLoad) return allowed;
  const exposed = { value: null, label: t.lockCalculatorModule.structural.lateralResistance.exposed };
  return [...allowed.filter(({ value }) => NOTCHES.includes(value)), exposed];
};

/**
 * Whether an option shows as selected. Without a lateral load "exposed" also stands for inclined fasteners and
 * lock stop: they are left as saved (no choice is lost while a load is retyped) but read as exposed.
 */
export const isLateralOptionSelected = (option: LateralResistanceOption, value: number | null, hasLateralLoad: boolean): boolean => {
  if (option.value !== null) return option.value === value;
  return value === null || (!hasLateralLoad && !NOTCHES.includes(value));
};

/** The value a click on an option sets: its own, or none when it is the one selected (exposed always means none). */
export const nextLateralConfig = (option: LateralResistanceOption, value: number | null): number | null =>
  option.value === value ? null : option.value;
